import {useField} from 'formik';
import _ from 'lodash';
import React, {useContext, useState} from 'react';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';

import {Badge, Btn, Icon, Main, Modal, Page, Spacer} from '../../../ui-toolkit/index';
import {Card} from '../../../ui-toolkit/indexExportV2';

import AuthContext from '../../../ducks/auth';
import {useEntities} from '../../../ducks/entitylist';
import {getForeignKeyEntityDataType} from '../../../ducks/formattersettingshelpers';
import {useTypeDetail} from '../../../ducks/typedetail-flattened';

import EntitiesList from '../../entity-list/EntitiesList';

import {useFormatterById} from '../../FormattersContext';

import ToJsonFormatter from './ToJsonFormatter';

import {getFkOptionsSpecification} from '../../../ducks/fkoptionshelper';
import {FormErrorMessage} from '../../../ui-toolkit/forms/form-features/FormErrorMessage';

import {ScopedPreferences} from '../../../ducks/PreferencesContext';
import {TableSelectedBadge} from '../../../ui-toolkit/page/page-features/PageContainerTypes';

function FKTableWrapper({
    entityType,
    formatter,
    fieldName,
    displayName,
    entityAttributeId,
    attributePath,
    parentTypeId,
    t,
    showButton,
    onClose,
}) {
    showButton = showButton !== false; // default to true
    let [show, setShow] = useState(!showButton);

    /** The entity data type that contains the attribute being edited. `null` while loading. */
    const parentDataType = useTypeDetail(parentTypeId);

    const [, meta, helpers] = useField(fieldName);
    const subFormatter = useFormatterById(formatter.settings.entryFormatterId);

    // Sometimes it seems like objects are passed instead of FK, this handles that case.
    // Additionally, a FK table allows selecting multiple entities, so we need to handle that case as well.
    const metaValue =
        meta.value !== null
            ? Array.isArray(meta.value)
                ? meta.value.map((x) => (typeof x === 'object' ? x.entityId : x))
                : typeof meta.value === 'object'
                ? meta.value.entityId
                : meta.value
            : null;

    const FieldFormatter = subFormatter ?? {Formatter: ToJsonFormatter};

    const childDataType = getForeignKeyEntityDataType(entityType);
    const selectedEntities = useEntities(childDataType.dataTypeId, metaValue);

    if (!parentDataType) return <p>{t('generalShared.loading')}</p>;

    const extraFilter = getFkOptionsSpecification(parentDataType, entityType, attributePath ?? entityAttributeId);

    function onCloseWrapper(ev) {
        if (_.isFunction(onClose)) onClose();
        setShow(false);
        if (ev?.stopPropagation) ev.stopPropagation();
    }

    function setValue(val, ev) {
        helpers.setValue(val);
        onCloseWrapper(ev);
    }

    return (
        <>
            {showButton && (
                <div>
                    <Btn type="button" ghost color="primary" onClick={() => setShow(!show)}>
                        <Icon type="sm" name="TouchApp" />
                        {t('editors.chooseEntity', {type: displayName})}
                    </Btn>

                    <div>
                        {!selectedEntities.loading &&
                            selectedEntities.entities &&
                            _.map(selectedEntities.entities, (ent) => (
                                <Wrapper key={_.get(ent, 'entityId')}>
                                    <Card size="sm">
                                        <FieldFormatter.Formatter
                                            entityType={entityType.entityDataType}
                                            formatter={subFormatter}
                                            entity={ent}
                                        />
                                    </Card>
                                </Wrapper>
                            ))}
                        {!selectedEntities.loading && _.isEmpty(selectedEntities.entities) && '-'}
                        {!!selectedEntities.loading && 'Loading...'}
                    </div>

                    {meta.error != null && <FormErrorMessage>{meta.error}</FormErrorMessage>}
                </div>
            )}

            {show && (
                <FKTableModal
                    setValue={setValue}
                    value={metaValue}
                    setError={helpers.setError}
                    error={meta.touched && meta.error}
                    onClose={onCloseWrapper}
                    entityType={entityType}
                    formatter={formatter}
                    displayName={displayName}
                    t={t}
                    extraFilter={extraFilter}
                />
            )}
        </>
    );
}

const getDataTypePath = (dataType) => {
    const dataTypePath = [];

    while (true) {
        dataTypePath.push(dataType);

        switch (dataType.discriminatorValue) {
            case 'ArrayDataType':
                dataType = dataType.childDataType;
                continue;
            case 'ForeignKeyDataType':
                dataType = dataType.entityDataType;
                continue;
            case 'NullableDataType':
                dataType = dataType.childDataType;
                continue;
            default:
                break;
        }

        break;
    }

    return dataTypePath;
};

function FKTableModal({entityType, formatter, setError, error, setValue, value, displayName, t, onClose, extraFilter}) {
    const auth = useContext(AuthContext);

    let nullable = _.get(formatter, 'settings.allowNull')?.toString() !== 'false';

    const dataTypePath = getDataTypePath(entityType);

    const childDataType = _.last(dataTypePath);
    const multiple = _.some(dataTypePath, {discriminatorValue: 'ArrayDataType'});

    let [selectedEntity, setSelectedEntity] = useState(value || (multiple ? [] : null));

    let entityDetail = useEntities(childDataType.dataTypeId, selectedEntity);

    // entry sub-formatter setup
    const subFormatter = useFormatterById(formatter.settings.entryFormatterId);

    let FieldFormatter = {Formatter: ToJsonFormatter};
    if (subFormatter) FieldFormatter = subFormatter;

    // list sub-formatter setup
    let listFormatterId = _.get(formatter, 'settings.listFormatterId', null);

    // stored filter state
    let preferenceScope = _.get(formatter, 'id', '-');

    function entityClick(typeId, entityId) {
        if (multiple) {
            if (!_.includes(selectedEntity, entityId)) {
                if (_.isEmpty(selectedEntity)) {
                    setSelectedEntity([entityId]);
                } else {
                    setSelectedEntity([entityId, ...selectedEntity]);
                }
            } else {
                setSelectedEntity(_.without(selectedEntity, entityId));
            }
        } else {
            if (nullable && selectedEntity === entityId) {
                setSelectedEntity(null);
            } else {
                setSelectedEntity(entityId);
            }
        }
    }

    function applyOptions(ev) {
        if (!nullable && multiple && selectedEntity.length === 0) {
            setError('Selectie vereist');
        } else if (!nullable && !multiple && selectedEntity === null) {
            setError('Selectie vereist');
        } else {
            setValue(selectedEntity, ev);
        }
    }

    return (
        <ScopedPreferences name={preferenceScope}>
            <Modal
                size="fixedHeight"
                title={t('editors.chooseEntity', {type: displayName})}
                show={true}
                onHide={onClose}>
                <Page>
                    <EntitiesList
                        auth={auth}
                        preferredFormatterId={listFormatterId}
                        entityType={childDataType}
                        entityClick={entityClick}
                        markedRows={multiple ? selectedEntity : [selectedEntity]}
                        extraFilter={extraFilter}
                        pageSize={300}
                    />
                    <TableSelectedBadge>
                        <Main.SubTitle>{t('editors.currentEntity')}</Main.SubTitle>
                        {entityDetail.entities &&
                            _.map(entityDetail.entities, (ent) => (
                                <Badge
                                    remove={() => entityClick(childDataType.dataTypeId, ent.entityId)}
                                    outline={true}
                                    color="default"
                                    key={_.get(ent, 'entityId')}>
                                    <FieldFormatter.Formatter
                                        entityType={entityType.entityDataType}
                                        formatter={subFormatter}
                                        entity={ent}
                                    />
                                </Badge>
                            ))}
                        {_.isEmpty(entityDetail.entities) && <p>-</p>}
                        {error != null && <FormErrorMessage>{error}</FormErrorMessage>}
                    </TableSelectedBadge>
                </Page>

                <Modal.Footer>
                    <Spacer.Align space="xsm">
                        <Btn color="success" type="button" onClick={applyOptions}>
                            <Icon name="check" />
                            {t('generalShared.apply')}
                        </Btn>
                        <Btn color="danger" ghost type="button" onClick={onClose}>
                            <Icon name="cancel" />
                            {t('generalShared.cancel')}
                        </Btn>
                    </Spacer.Align>
                </Modal.Footer>
            </Modal>
        </ScopedPreferences>
    );
}

export default withTranslation()(FKTableWrapper);

const Wrapper = styled.div`
    ${Card.Wrapper} {
        display: inline-block;
        margin: 8px 8px 0 0;
    }
`;
